import React from 'react';
import he from 'he';
import { camelizeKeys } from 'humps';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client/react/hooks';
import { toGlobalId } from 'graphql-relay';

import Image from 'lib/images/image-view';
import { WPAttachment } from 'lib/wp/wp-attachment';

import BlogLink from 'four-nets/blog/blog-link';

import defaultStyles from './related-magazine-posts.scss';
import query, { RelatedMagazinePostsQuery, RelatedMagazinePostsQueryVariables } from './related-magazine-posts.graphql';

interface Props {
    styles: typeof defaultStyles;
    category: { id: number };
}

interface RelatedMagazinePostProps {
    id: number;
    title: { rendered: string };
    categories: [{ id: number; name: string; slug: string }];
    slug: string;
    featured_media: any;
    styles: typeof defaultStyles;
}

const _RelatedMagazinePost: React.FC<React.PropsWithChildren<RelatedMagazinePostProps>> = props => {
    const { styles, title, featured_media: featuredMedia, categories, slug } = props;
    const firstCategory = categories[0];
    const image = new WPAttachment({});
    image.fromJSON({ json: camelizeKeys(featuredMedia) });
    return (
        <div className={styles.post}>
            <BlogLink to={`/magazine/${slug}`}>
                <Image
                    className={styles.postImage}
                    linkify={false}
                    aspectRatio={9 / 16}
                    aspectFill={true}
                    src={featuredMedia.source_url}
                    sizes={{
                        xs: 165,
                        md: 255,
                    }}
                />
            </BlogLink>
            <BlogLink className={styles.categoryLink} to={`/magazine/${firstCategory.slug}`}>
                {firstCategory.name}
            </BlogLink>
            <div>
                <BlogLink className={styles.postLink} to={`/magazine/${slug}`}>
                    {he.decode(title.rendered)}
                </BlogLink>
            </div>
        </div>
    );
};

export const RelatedMagazinePost = Styled(defaultStyles)(_RelatedMagazinePost);

const RelatedMagazinePosts: React.FC<Props> = props => {
    const { styles, category } = props;
    const { data } = useQuery<RelatedMagazinePostsQuery, RelatedMagazinePostsQueryVariables>(query, {
        variables: { threadCategoryId: toGlobalId('ThreadCategoryNode', category.id.toString()) },
    });
    const posts = data ? JSON.parse(data?.threadMagazinePosts) : [];
    return posts ? (
        <div>
            <FormattedMessage id='ARTICLES_FROM'>
                {msg => (
                    <h3 className={styles.heading}>
                        {`${msg} `}
                        <FormattedMessage id='OF_MAGAZINE'>
                            {msg2 => (
                                <BlogLink to='/magazine'>
                                    {msg2}
                                </BlogLink>
                            )}
                        </FormattedMessage>
                    </h3>
                )}
            </FormattedMessage>
            <div className={styles.posts}>
                {posts?.map((post: RelatedMagazinePostProps) => (
                    <RelatedMagazinePost key={post.id} {...post} />
                ))}
            </div>
        </div>
    ) : null;
};

export default Styled(defaultStyles)(RelatedMagazinePosts);
